(() => {
  const AUDIENCE_KEY = 'filter.p.m.custom.audience_reference';
  const ROOT_TAGS = {
    bekleidung: 'category:bekleidung_lvl_1',
    schuhe: 'category:schuhe_lvl_1',
    ausrustung: 'category:ausruestung_lvl_1'
  };
  const availabilityMemory = new Map();

  const registry = () => window.PeakStyleCategoryRegistry;
  const nodeHasProducts = (node) => Number(node.count) > 0 || (node.children || []).some(nodeHasProducts);

  const audienceValues = (panel) => {
    const source = panel.querySelector('.ps-mega__all-link[href]');
    if (!source) return [];
    const url = new URL(source.getAttribute('href'), window.location.origin);
    const repeated = url.searchParams.getAll(AUDIENCE_KEY);
    if (repeated.length > 1) return repeated;
    return repeated.flatMap((value) => String(value || '').split(',')).filter(Boolean);
  };

  const withAudience = (href, values) => registry()?.withAudience(href, values) || href;

  const createBranch = (node, values, depth) => {
    const item = document.createElement('li');
    item.className = 'ps-mega__category-node';
    item.dataset.psCategoryNode = node.key;
    item.dataset.psCategoryUrl = withAudience(node.url, values);
    item.style.setProperty('--ps-category-depth', String(depth));

    const row = document.createElement('div');
    row.className = 'ps-mega__category-node-row';
    const link = document.createElement('a');
    link.href = item.dataset.psCategoryUrl;
    link.textContent = node.title;
    link.dataset.psCategoryLink = 'true';
    row.appendChild(link);

    const children = (node.children || []).filter(nodeHasProducts);
    if (children.length) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'ps-mega__category-node-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', node.title + ' Unterkategorien öffnen');
      toggle.innerHTML = '<span aria-hidden="true"></span>';
      const list = document.createElement('ul');
      list.className = 'ps-mega__category-sublist';
      list.hidden = true;
      children.forEach((child) => list.appendChild(createBranch(child, values, depth + 1)));
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        list.hidden = !open;
      });
      row.appendChild(toggle);
      item.append(row, list);
    } else {
      item.appendChild(row);
    }
    return item;
  };

  const createGroup = (node, values) => {
    const group = document.createElement('div');
    group.className = 'ps-mega__category-group';
    group.dataset.psCategoryNode = node.key;
    group.dataset.psCategoryUrl = withAudience(node.url, values);

    const heading = document.createElement('h3');
    const headingLink = document.createElement('a');
    headingLink.href = group.dataset.psCategoryUrl;
    headingLink.textContent = node.title;
    headingLink.dataset.psCategoryLink = 'true';
    heading.appendChild(headingLink);
    group.appendChild(heading);

    const children = (node.children || []).filter(nodeHasProducts);
    if (children.length) {
      const list = document.createElement('ul');
      list.className = 'ps-mega__category-sublist ps-mega__category-sublist--root';
      children.forEach((child) => list.appendChild(createBranch(child, values, 3)));
      group.appendChild(list);
    }
    return group;
  };

  const patchPanel = (panel, rootNode) => {
    const container = panel?.querySelector('.ps-mega__category-groups');
    if (!container || !rootNode || container.dataset.psCentralCategories === 'true') return;
    const allGroup = container.querySelector(':scope > .ps-mega__category-group--all');
    const values = audienceValues(panel);
    const fragment = document.createDocumentFragment();
    if (allGroup) fragment.appendChild(allGroup);
    (rootNode.children || []).filter(nodeHasProducts).forEach((node) => fragment.appendChild(createGroup(node, values)));
    container.replaceChildren(fragment);
    container.dataset.psCentralCategories = 'true';
  };

  const patch = () => {
    const data = registry();
    if (!data?.tree?.length) return;
    Object.entries(ROOT_TAGS).forEach(([category, tag]) => {
      const rootNode = data.tree.find((node) => node.tag === tag);
      document.querySelectorAll('.ps-mega__panel[data-panel$=":' + category + '"]').forEach((panel) => patchPanel(panel, rootNode));
    });
  };

  const availability = async (rawUrl) => {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('page');
    const key = url.pathname + '?' + url.searchParams.toString();
    if (availabilityMemory.has(key)) return availabilityMemory.get(key);
    try {
      const cached = sessionStorage.getItem('ps-cat:' + key);
      if (cached === '1' || cached === '0') {
        const value = cached === '1';
        availabilityMemory.set(key, value);
        return value;
      }
    } catch (error) {}

    const promise = fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.ok ? response.text() : '')
      .then((html) => {
        const match = html.match(/data-peakstyle-category-navigation[^>]*data-results-count="(\d+)"/i);
        const available = match ? Number(match[1]) > 0 : true;
        try { sessionStorage.setItem('ps-cat:' + key, available ? '1' : '0'); } catch (error) {}
        availabilityMemory.set(key, available);
        return available;
      })
      .catch(() => true);
    availabilityMemory.set(key, promise);
    return promise;
  };

  const verifyPanel = async (panel) => {
    if (!panel || panel.dataset.psAvailabilityChecked === 'true' || panel.dataset.psAvailabilityChecking === 'true') return;
    panel.dataset.psAvailabilityChecking = 'true';
    const elements = [...panel.querySelectorAll('[data-ps-category-url]')];
    let index = 0;
    const worker = async () => {
      while (index < elements.length) {
        const element = elements[index++];
        const available = await availability(element.dataset.psCategoryUrl);
        if (!available) element.remove();
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, elements.length) }, worker));
    panel.dataset.psAvailabilityChecking = 'false';
    panel.dataset.psAvailabilityChecked = 'true';
  };

  const init = () => { patch(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('peakstyle:category-registry-ready', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('mouseover', (event) => {
    const panel = event.target.closest?.('.ps-mega__panel[data-panel]');
    if (panel && !panel.hidden && /:(bekleidung|schuhe|ausrustung)$/.test(panel.dataset.panel || '')) verifyPanel(panel);
  }, true);
})();